import React from 'react';
import { Lightbulb, Edit3, CheckCircle, GitBranch, MessageCircle } from 'lucide-react';
import { StepIntent, StepType } from '../../types';
import Tooltip from '../common/Tooltip';

interface StepIntentSelectorProps {
  selectedIntent?: StepIntent;
  onSelect: (intent: StepIntent | undefined) => void;
  validIntents?: StepIntent[]; // Array of valid intents to filter by
  isLoading?: boolean; // Loading state for intent validation
  prevStepType?: StepType; // Previous step type for smart suggestions
}

const intentConfig = {
  create: {
    name: 'Create',
    description: 'Generate new content or ideas',
    icon: Lightbulb,
    color: 'bg-primary-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    hoverColor: 'hover:bg-primary-100 dark:hover:bg-blue-900/40',
  },
  enhance: {
    name: 'Enhance',
    description: 'Improve or extend existing content',
    icon: Edit3,
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    hoverColor: 'hover:bg-purple-100 dark:hover:bg-purple-900/40',
  },
  validate: {
    name: 'Validate',
    description: 'Check quality or correctness',
    icon: CheckCircle,
    color: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/40',
  },
  conditional: {
    name: 'Conditional',
    description: 'Make decisions based on conditions',
    icon: GitBranch,
    color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/40',
  },
  assistant: {
    name: 'Assistant',
    description: 'Interactive conversational agent',
    icon: MessageCircle,
    color: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    hoverColor: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/40',
  },
};

const StepIntentSelector: React.FC<StepIntentSelectorProps> = ({
  selectedIntent,
  onSelect,
  validIntents,
  isLoading = false,
  prevStepType,
}) => {
  // Filter intents based on validIntents prop
  const availableIntents = validIntents
    ? Object.entries(intentConfig).filter(([key]) => validIntents.includes(key as StepIntent))
    : Object.entries(intentConfig);

  // Auto-select the first available intent when none is selected
  const firstAvailableIntent = availableIntents.length > 0 ? availableIntents[0][0] as StepIntent : undefined;

  React.useEffect(() => {
    if (!selectedIntent && firstAvailableIntent) {
      onSelect(firstAvailableIntent);
    }
  }, [selectedIntent, firstAvailableIntent, onSelect]);

  if (isLoading) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Step Intent
        </label>
        <div className="flex items-center justify-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading valid intents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Step Intent <span className="text-red-600">*</span>
      </label>

      {availableIntents.length === 0 && !isLoading && (
        <div className="p-4 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="ml-2 text-sm text-amber-700 dark:text-amber-300">
              No valid intents available for this workflow position.
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {availableIntents.map(([key, config]) => {
          const Icon = config.icon;
          const intent = key as StepIntent;
          const isSelected = selectedIntent === intent;
          const isDisabled = validIntents && !validIntents.includes(intent);

          return (
            <Tooltip key={key} content={isDisabled ? 'This intent is not available for this workflow position' : ''}>
            <button
              type="button"
              disabled={isDisabled}
              onClick={() => {
                console.log('[StepIntentSelector] Intent selected:', intent);
                onSelect(intent);
              }}
              data-tutorial={`step-option-run_agent-${key}`}
              className={`
                p-3 rounded-lg border-2 text-left transition-all
                ${isSelected
                  ? `${config.color} border-current`
                  : isDisabled
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : `border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900`
                }
                ${!isDisabled ? config.hoverColor : ''}
              `}

            >
              <div className="flex items-start space-x-2">
                <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-sm">{config.name}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {config.description}
                  </div>
                </div>
              </div>
            </button>
            </Tooltip>
          );
        })}
      </div>



    </div>
  );
};

export default StepIntentSelector;